/**
 * Cache binaires API (pièces jointes rapport) — GO_OFFLINE_MEDIA_API
 * Partagé app + service worker (`ucp-api-media-v1`).
 */
import {
  appendAuthTokenToApiAssetUrl,
  appendStrictFallbackParam,
  readAuthTokenForAsset
} from './attachmentAssetUrl';

export const API_MEDIA_CACHE_NAME = 'ucp-api-media-v1';

const CONTENT_PATH_RE =
  /\/api\/intervention-reports\/[^/]+\/images\/(?:by-filename\/[^/]+|[^/]+)\/content$/i;

export function isInterventionReportImageContentPath(urlOrPath) {
  const raw = String(urlOrPath || '').trim();
  if (!raw) return false;
  let path;
  try {
    path = raw.includes('://') ? new URL(raw).pathname : raw.split('?')[0];
  } catch {
    path = raw.split('?')[0];
  }
  if (!path.startsWith('/')) path = `/${path}`;
  if (path.startsWith('/intervention-reports/')) path = `/api${path}`;
  return CONTENT_PATH_RE.test(path.replace(/\/+$/, ''));
}

/** URL clé cache = origine page + pathname (sans query token). */
export function toApiMediaCacheKeyUrl(urlOrPath) {
  if (typeof window === 'undefined') return '';
  let path = String(urlOrPath || '').trim();
  if (!path) return '';
  try {
    if (path.includes('://')) {
      const u = new URL(path);
      path = u.pathname;
    } else {
      path = path.split('?')[0];
    }
  } catch {
    path = path.split('?')[0];
  }
  if (!path.startsWith('/')) path = `/${path}`;
  if (path.startsWith('/intervention-reports/')) path = `/api${path}`;
  if (!path.startsWith('/api/')) return '';
  return `${window.location.origin}${path.replace(/\/+$/, '')}`;
}

export function extractReportImageRows(payload) {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.images)) return payload.images;
  if (Array.isArray(payload.attachments)) return payload.attachments;
  if (Array.isArray(payload.data)) return payload.data;
  return [];
}

async function openMediaCache() {
  if (typeof caches === 'undefined') return null;
  try {
    return await caches.open(API_MEDIA_CACHE_NAME);
  } catch {
    return null;
  }
}

export async function matchApiMediaCache(urlOrPath) {
  const keyUrl = toApiMediaCacheKeyUrl(urlOrPath);
  if (!keyUrl) return null;
  const cache = await openMediaCache();
  if (!cache) return null;
  try {
    const byKey = await cache.match(keyUrl, { ignoreSearch: true });
    if (byKey) return byKey;
    return cache.match(new Request(keyUrl), { ignoreSearch: true });
  } catch {
    return null;
  }
}

export async function putApiMediaCacheFromResponse(urlOrPath, response) {
  if (!response || !response.ok) return false;
  const keyUrl = toApiMediaCacheKeyUrl(urlOrPath);
  if (!keyUrl) return false;
  const cache = await openMediaCache();
  if (!cache) return false;
  try {
    await cache.put(keyUrl, response.clone());
    return true;
  } catch (err) {
    console.warn('[UCP] API media cache put failed:', err?.message || err);
    return false;
  }
}

export async function putApiMediaCacheFromBlob(urlOrPath, blob, contentType) {
  if (!blob || typeof Response === 'undefined') return false;
  const type = contentType || blob.type || 'application/octet-stream';
  const response = new Response(blob, {
    status: 200,
    statusText: 'OK',
    headers: { 'Content-Type': type }
  });
  return putApiMediaCacheFromResponse(urlOrPath, response);
}

/**
 * Prefetch same-origin (credentials + token query) → Cache API.
 * @returns {Promise<boolean>}
 */
export async function warmApiMediaUrl(urlOrPath) {
  if (typeof window === 'undefined' || typeof fetch !== 'function') return false;
  const keyUrl = toApiMediaCacheKeyUrl(urlOrPath);
  if (!keyUrl || !isInterventionReportImageContentPath(keyUrl)) return false;

  const existing = await matchApiMediaCache(keyUrl);
  if (existing) return true;

  let fetchUrl = keyUrl;
  fetchUrl = appendStrictFallbackParam(fetchUrl);
  fetchUrl = appendAuthTokenToApiAssetUrl(fetchUrl);
  // Si pas de token en query, cookie ucp_token peut suffire (credentials include).
  if (!readAuthTokenForAsset() && !/[?&]token=/i.test(fetchUrl)) {
    /* credentials: 'include' ci-dessous */
  }

  try {
    const response = await fetch(fetchUrl, {
      method: 'GET',
      credentials: 'include',
      cache: 'no-store'
    });
    if (!response.ok) return false;
    return putApiMediaCacheFromResponse(keyUrl, response);
  } catch {
    return false;
  }
}
